import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button, EmptyState, Skeleton } from '@mailflow/ui';
import { api } from '../lib/api.js';

export interface QueueItem {
  id: string;
  contactId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  campaignId: string | null;
  campaignName: string | null;
  status: 'queued' | 'sending' | 'sent' | 'delivered' | 'bounced' | 'failed' | 'skipped';
  scheduledFor: string;
  sentAt: string | null;
  skipReason: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface QueueSummary {
  queuedTotal: number;
  queuedToday: number;
  sentToday: number;
  failedTotal: number;
  policy: {
    name: string;
    warmupEnabled: boolean;
    warmupStart: number;
    warmupMultiplier: number;
    warmupCap: number;
    emailsPerHour: number;
    emailsPerDay: number;
    sendWindows: Array<{ day: number; start: string; end: string }>;
  } | null;
}

export interface QueueResponse {
  summary: QueueSummary;
  items: QueueItem[];
}

const STATUS_LABELS: Record<string, string> = {
  queued: 'In Warteschlange',
  sending: 'Wird versendet …',
  sent: 'Versendet',
  delivered: 'Zugestellt',
  bounced: 'Bounced',
  failed: 'Fehlgeschlagen',
  skipped: 'Übersprungen',
};

const STATUS_BADGE_VARIANT: Record<string, string> = {
  queued: 'primary',
  sending: 'warning',
  sent: 'success',
  delivered: 'success',
  bounced: 'danger',
  failed: 'danger',
  skipped: 'neutral',
};

const SKIP_REASONS: Record<string, string> = {
  replied: 'Lead hat geantwortet (Auto-Stop)',
  unsubscribed: 'Abgemeldet',
  suppressed: 'Auf Sperrliste',
  bounced: 'Adresse unzustellbar',
  frequency_cap: 'Frequenzbegrenzung',
  duplicate: 'Bereits kontaktiert',
};

export function QueuePage() {
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  const { data, isLoading, refetch, isFetching } = useQuery<QueueResponse>({
    queryKey: ['campaigns-queue', filterStatus, search],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (search.trim()) params.set('search', search.trim());
      params.set('limit', '100');
      return api.get<QueueResponse>(`/api/campaigns/queue?${params.toString()}`, signal);
    },
    refetchInterval: 8000, // Alle 8 Sekunden live aktualisieren
  });

  const summary = data?.summary;
  const items = data?.items ?? [];

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      {/* KOPFZEILE */}
      <header className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
        <div>
          <h1 className="page-header__title">Warteschlange & Sendeplan</h1>
          <p className="page-header__description">
            Echtzeit-Übersicht aller anstehenden, geplanten und versendeten E-Mails.
          </p>
        </div>
        <div className="cluster" style={{ gap: 'var(--space-2)' }}>
          <Button
            variant="secondary"
            onClick={() => void refetch()}
            loading={isFetching}
          >
            🔄 Aktualisieren
          </Button>
          <Link to="/kampagnen" className="btn btn--secondary">
            Kampagnen verwalten
          </Link>
        </div>
      </header>

      {/* METRIKEN KACHELN */}
      <div className="grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--space-4)' }}>
        <div className="card">
          <div className="card__body stack" style={{ gap: '4px' }}>
            <span className="sidebar__label">In Warteschlange</span>
            <div style={{ fontSize: '1.85rem', fontWeight: 700, color: 'var(--color-primary)' }}>
              {isLoading ? '…' : summary?.queuedTotal ?? 0}
            </div>
            <span className="muted text-xs">Warten auf Versandfenster</span>
          </div>
        </div>

        <div className="card">
          <div className="card__body stack" style={{ gap: '4px' }}>
            <span className="sidebar__label">Heute fällig</span>
            <div style={{ fontSize: '1.85rem', fontWeight: 700, color: 'var(--color-text)' }}>
              {isLoading ? '…' : summary?.queuedToday ?? 0}
            </div>
            <span className="muted text-xs">Im heutigen Tageskontingent</span>
          </div>
        </div>

        <div className="card">
          <div className="card__body stack" style={{ gap: '4px' }}>
            <span className="sidebar__label">Heute versendet</span>
            <div style={{ fontSize: '1.85rem', fontWeight: 700, color: '#10b981' }}>
              {isLoading ? '…' : summary?.sentToday ?? 0}
            </div>
            <span className="muted text-xs">Erfolgreich zugestellt</span>
          </div>
        </div>

        <div className="card">
          <div className="card__body stack" style={{ gap: '4px' }}>
            <span className="sidebar__label">Sendeprofil & Zeiten</span>
            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-text)', marginTop: '4px' }}>
              {summary?.policy?.name ?? 'Standard Profil'}
            </div>
            <span className="muted text-xs">
              Mo–Fr 07:45 – 11:50 Uhr (Europe/Zurich)
            </span>
          </div>
        </div>
      </div>

      {/* FILTER & SUCHE */}
      <div className="card">
        <div className="card__body stack" style={{ gap: 'var(--space-4)' }}>
          <div className="cluster" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            {/* STATUS TABS */}
            <div className="chip-row" role="group" aria-label="Status Filter">
              {[
                { id: 'all', label: 'Alle' },
                { id: 'queued', label: 'In Warteschlange' },
                { id: 'sent', label: 'Versendet' },
                { id: 'skipped', label: 'Übersprungen' },
                { id: 'failed', label: 'Fehler' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className="chip"
                  aria-pressed={filterStatus === tab.id}
                  onClick={() => setFilterStatus(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* SUCHE */}
            <div style={{ minWidth: '260px' }}>
              <input
                type="search"
                className="input"
                placeholder="Empfänger, Name oder Firma suchen …"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* TABELLE */}
          {isLoading ? (
            <Skeleton lines={6} shape="row" label="Warteschlange wird geladen …" />
          ) : items.length === 0 ? (
            <EmptyState title="Keine E-Mails in der Warteschlange" symbol="✓">
              {filterStatus === 'all'
                ? 'Aktuell sind keine E-Mails eingereiht. Wenn Sie eine Kampagne starten, erscheinen die geplanten Versendungen hier.'
                : `Keine Einträge für den Filter „${filterStatus}".`}
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Empfänger</th>
                    <th>Kampagne</th>
                    <th>Geplanter Versand</th>
                    <th>Status</th>
                    <th>Details / Ergebnis</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <div className="stack" style={{ gap: '2px' }}>
                          <Link
                            to="/kontakte/$id"
                            params={{ id: item.contactId }}
                            style={{ fontWeight: 600, color: 'var(--color-text)', textDecoration: 'none' }}
                          >
                            {item.email}
                          </Link>
                          {(item.firstName || item.lastName || item.companyName) && (
                            <span className="muted text-xs">
                              {[item.firstName, item.lastName].filter(Boolean).join(' ')}
                              {item.companyName ? ` · ${item.companyName}` : ''}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {item.campaignName ? (
                          <span style={{ fontSize: '0.9rem' }}>{item.campaignName}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        <span style={{ fontSize: '0.85rem' }}>
                          {new Date(item.scheduledFor).toLocaleString('de-CH', {
                            weekday: 'short',
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </td>
                      <td>
                        <span
                          className="badge"
                          data-status={item.status === 'sent' || item.status === 'delivered' ? 'active' : item.status === 'queued' ? 'active' : 'paused'}
                          style={{
                            fontSize: '0.75rem',
                            padding: '3px 8px',
                            background: item.status === 'queued' ? 'rgba(59, 130, 246, 0.15)' : undefined,
                            color: item.status === 'queued' ? '#3b82f6' : undefined,
                          }}
                        >
                          {STATUS_LABELS[item.status] ?? item.status}
                        </span>
                      </td>
                      <td>
                        {item.status === 'sent' && item.sentAt && (
                          <span className="muted text-xs">
                            Versendet am {new Date(item.sentAt).toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </span>
                        )}
                        {item.status === 'skipped' && (
                          <span className="text-xs" style={{ color: '#eab308' }}>
                            {SKIP_REASONS[item.skipReason ?? ''] ?? item.skipReason ?? 'Übersprungen'}
                          </span>
                        )}
                        {item.status === 'failed' && (
                          <span className="text-xs" style={{ color: '#ef4444' }}>
                            {item.errorMessage ?? 'Fehler beim Sendeversuch'}
                          </span>
                        )}
                        {item.status === 'queued' && (
                          <span className="muted text-xs">
                            Wartet im Sendeplan
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
